import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  TextField,
  Typography,
  LinearProgress,
  AppBar,
  Toolbar,
  Paper,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Stack,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { styled } from '@mui/system';
import Selector from '../../components/Selector';
import SitemarkIcon from '../../components/SitemarkIcon';
import { tiposCompania, monedas, paises } from '../../utils/staticvariables';

const Header = styled(AppBar)({
  backgroundColor: 'white',
  boxShadow: 'none',
  borderBottom: '1px solid #e7edf4',
  padding: '12px 40px',
});

const SectionCard = styled(Paper)({
  padding: '32px',
  borderRadius: 16,
  border: '1px solid #e7edf4',
  boxShadow: '0 1px 2px rgba(16, 20, 24, 0.04)',
  backgroundColor: 'white',
});

const SectionIcon = styled(Box)({
  width: 40,
  height: 40,
  borderRadius: 10,
  backgroundColor: '#eff4fb',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#3f6ed8',
});

const Step1CompanyInfo = ({ form, setForm, onBack, onNext }) => {
  // Campos
  const [companyName, setCompanyName] = useState(form.general ? form.general.companyName || '' : '');
  const [taxId, setTaxId] = useState(form.general ? form.general.taxId || '' : '');
  const [phone, setPhone] = useState(form.general ? form.general.phone || '' : '');
  const [address, setAddress] = useState(form.general ? form.general.address || '' : '');
  const [currency, setCurrency] = useState(form.general ? form.general.currency || '' : '');
  const [country, setCountry] = useState(form.general ? form.general.country || '' : '');
  const [companyTypes, setCompanyTypes] = useState(tiposCompania);

  const [companyTypesSelected, setCompanyTypesSelected] = useState(form.general ? form.general.companyTypes || [] : []);

  const adminInicial = form.usuarios?.administrador || {};
  const [nombreCompleto, setNombreCompleto] = useState(adminInicial.nombre_completo || '');
  const [username, setUsername] = useState(adminInicial.nombre || '');
  const [password, setPassword] = useState(adminInicial.contrasena || '');


  // Errores
  const [errors, setErrors] = useState({});

  // Manejo de errores individuales
  const clearError = (field) => {
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleNext = () => {
    const newErrors = {};

    if (!companyName.trim()) newErrors.companyName = 'Este campo es obligatorio';
    if (companyTypesSelected.length === 0) newErrors.companyTypesSelected = 'Seleccione al menos una opción';
    if (!taxId.trim()) newErrors.taxId = 'Este campo es obligatorio';
    if (!phone.trim()) newErrors.phone = 'Este campo es obligatorio';
    if (!address.trim()) newErrors.address = 'Este campo es obligatorio';
    if (!currency) newErrors.currency = 'Seleccione una opción';
    if (!country) newErrors.country = 'Seleccione una opción';
    if (!nombreCompleto.trim()) newErrors.nombreCompleto = 'Este campo es obligatorio';
    if (!username.trim()) newErrors.username = 'Este campo es obligatorio';
    if (!password.trim()) newErrors.password = 'Este campo es obligatorio';

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setForm({
        ...form,
        general: {
          companyName,
          companyTypes: companyTypesSelected,
          taxId,
          phone,
          address,
          currency,
          country,
        },
        sucursales: [{
          id: 'principal',
          nombre: companyName,
          direccion: address,
          contacto: phone,
        }],
        usuarios: {
          administrador: {
            nombre_completo: nombreCompleto,
            nombre: username,
            contrasena: password,
            sucursal: '',
          },
        },
      });
      onNext();
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f8fafc',
        fontFamily: 'Inter, Noto Sans, sans-serif',
      }}
    >
      <Header position="sticky">
        <Toolbar sx={{ justifyContent: 'space-between', px: '0 !important' }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box width={28} height={28}>
              <SitemarkIcon />
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Paso 1 de 3
          </Typography>
        </Toolbar>
      </Header>

      <Container maxWidth="md" sx={{ py: 6 }}>
        <Box sx={{ mb: 5 }}>
          <Stepper activeStep={0} alternativeLabel>
            <Step><StepLabel>Tu empresa</StepLabel></Step>
            <Step><StepLabel>Módulos</StepLabel></Step>
            <Step><StepLabel>Revisar</StepLabel></Step>
          </Stepper>
        </Box>

        <Box textAlign="center" mb={5}>
          <Typography variant="h4" fontWeight={700} color="#101418" gutterBottom>
            Cuéntanos de tu empresa
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto' }}>
            Con esta información configuraremos automáticamente tu sucursal principal y tus productos financieros iniciales.
          </Typography>
        </Box>

        <SectionCard elevation={0} sx={{ mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center" mb={3}>
            <SectionIcon><BusinessIcon /></SectionIcon>
            <Box>
              <Typography variant="h6" fontWeight={700} color="#101418">
                Información de la empresa
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Datos generales y tipo de financiera
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" fontWeight={500} color="#101418" mb={1} textAlign="left">
                Nombre de la empresa
              </Typography>
              <TextField
                fullWidth
                value={companyName}
                onChange={(e) => { setCompanyName(e.target.value); clearError('companyName'); }}
                error={!!errors.companyName}
                helperText={errors.companyName}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth error={!!errors.companyTypesSelected}>
                <Typography variant="body2" fontWeight={500} color="#101418" mb={1} textAlign="left">
                  Tipo de financiera
                </Typography>
                <Selector
                  multiple
                  value={companyTypesSelected}
                  onChange={(e) => { setCompanyTypesSelected(e.target.value); clearError('companyTypesSelected'); }}
                  options={companyTypes}
                  placeholder="Seleccione uno o varios tipos"
                  error={!!errors.companyTypesSelected}
                />
                {errors.companyTypesSelected && (
                  <Typography variant="caption" color="error" textAlign="start" sx={{ mt: 0.5, px: 2 }}>
                    {errors.companyTypesSelected}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, px: 1 }} textAlign="left">
                  Según los tipos que elijas pre-cargaremos productos y categorías relevantes.
                </Typography>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" fontWeight={500} color="#101418" mb={1} textAlign="left">
                Identificación fiscal
              </Typography>
              <TextField
                fullWidth
                value={taxId}
                onChange={(e) => { setTaxId(e.target.value); clearError('taxId'); }}
                error={!!errors.taxId}
                helperText={errors.taxId}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" fontWeight={500} color="#101418" mb={1} textAlign="left">
                Teléfono
              </Typography>
              <TextField
                fullWidth
                value={phone}
                onChange={(e) => { setPhone(e.target.value); clearError('phone'); }}
                error={!!errors.phone}
                helperText={errors.phone}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" fontWeight={500} color="#101418" mb={1} textAlign="left">
                Dirección
              </Typography>
              <TextField
                fullWidth
                value={address}
                onChange={(e) => { setAddress(e.target.value); clearError('address'); }}
                error={!!errors.address}
                helperText={errors.address}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!errors.currency}>
                <Typography variant="body2" fontWeight={500} color="#101418" mb={1} textAlign="left">
                  Moneda
                </Typography>
                <Selector
                  value={currency}
                  onChange={(e) => { setCurrency(e.target.value); clearError('currency'); }}
                  options={monedas}
                  placeholder="Seleccione una moneda"
                  error={!!errors.currency}
                />
                {errors.currency && (
                  <Typography variant="caption" color="error" textAlign="start" sx={{ mt: 0.5, px: 2 }}>
                    {errors.currency}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!errors.country}>
                <Typography variant="body2" fontWeight={500} color="#101418" mb={1} textAlign="left">
                  País
                </Typography>
                <Selector
                  value={country}
                  onChange={(e) => { setCountry(e.target.value); clearError('country'); }}
                  options={paises}
                  placeholder="Seleccione un país"
                  error={!!errors.country}
                />
                {errors.country && (
                  <Typography variant="caption" color="error" textAlign="start" sx={{ mt: 0.5, px: 2 }}>
                    {errors.country}
                  </Typography>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard elevation={0}>
          <Stack direction="row" spacing={2} alignItems="center" mb={3}>
            <SectionIcon><PersonOutlineIcon /></SectionIcon>
            <Box>
              <Typography variant="h6" fontWeight={700} color="#101418">
                Usuario administrador
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Crearemos tu primer usuario y una sucursal principal automáticamente
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" fontWeight={500} color="#101418" mb={1} textAlign="left">
                Nombre completo
              </Typography>
              <TextField
                fullWidth
                value={nombreCompleto}
                onChange={(e) => { setNombreCompleto(e.target.value); clearError('nombreCompleto'); }}
                error={!!errors.nombreCompleto}
                helperText={errors.nombreCompleto}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" fontWeight={500} color="#101418" mb={1} textAlign="left">
                Nombre de usuario
              </Typography>
              <TextField
                fullWidth
                value={username}
                onChange={(e) => { setUsername(e.target.value); clearError('username'); }}
                error={!!errors.username}
                helperText={errors.username}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" fontWeight={500} color="#101418" mb={1} textAlign="left">
                Contraseña
              </Typography>
              <TextField
                type="password"
                fullWidth
                value={password}
                onChange={(e) => { setPassword(e.target.value); clearError('password'); }}
                error={!!errors.password}
                helperText={errors.password}
              />
            </Grid>
          </Grid>
        </SectionCard>

        <Box mt={4}>
          <LinearProgress variant="determinate" value={33} sx={{ bgcolor: '#e7edf4', height: 6, borderRadius: 1 }} />
        </Box>

        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            sx={{ fontWeight: 600, px: 4, py: 1.2, borderRadius: 2, textTransform: 'none' }}
            onClick={handleNext}
          >
            Continuar
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Step1CompanyInfo;
